import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAcademy } from '../AcademyContext';
import { IconCheck, IconLock, IconQuiz, IconLab } from './Icons';

const MODE_LABEL = { easy: 'Easy Mode', hard: 'Hard Mode', subject: 'Subject Line Mode' };

/**
 * Course path preview (sidebar on the lesson page). Shows the full ordered
 * stack — videos AND the mini-quizzes / tasks between them — with progressive
 * unlocking. Locked steps are dimmed and not clickable.
 */
const CourseSidebar = ({ topicId, course, activeLessonId }) => {
  const { getCourseSteps } = useAcademy();
  const steps = getCourseSteps(topicId, course);

  const listRef = useRef(null);
  const focusRef = useRef(null);

  // Focus the current task: prefer the active (next) step, else the lesson
  // currently being viewed.
  const focusKey = (
    steps.find((s) => s.active) ||
    steps.find((s) => s.type === 'video' && s.lesson.id === activeLessonId)
  )?.key;

  // On load, scroll the current task to the top of the sidebar list.
  useEffect(() => {
    const list = listRef.current;
    const el = focusRef.current;
    if (!list || !el) return;
    list.scrollTop += el.getBoundingClientRect().top - list.getBoundingClientRect().top;
  }, [focusKey]);

  let videoNo = 0;

  return (
    <aside className="ac-side">
      <p className="ac-side-title">Course Path</p>
      <div className="ac-side-list" ref={listRef}>
        {steps.map((step) => {
          const { lesson, type, done, locked, active } = step;

          if (type === 'video') {
            videoNo += 1;
            const isCurrent = lesson.id === activeLessonId;
            const stateClass = `${isCurrent ? 'is-active' : ''} ${locked ? 'is-locked' : ''} ${
              active && !isCurrent ? 'is-next' : ''
            }`;
            const thumb = (
              <>
                <div className="ac-side-thumb">
                  <img src={lesson.preview} alt={lesson.title} className="ac-side-thumb-img" />
                  <span className="ac-side-thumb-index">{videoNo}</span>
                  <span className="ac-side-thumb-dur">{lesson.duration}</span>
                  {done && (
                    <span className="ac-side-thumb-check">
                      <IconCheck size={11} />
                    </span>
                  )}
                  {locked && (
                    <span className="ac-side-thumb-locked">
                      <IconLock size={16} />
                    </span>
                  )}
                </div>
                <p className="ac-side-item-title">{lesson.title}</p>
              </>
            );
            return locked ? (
              <div
                key={step.key}
                ref={step.key === focusKey ? focusRef : undefined}
                className={`ac-side-item ${stateClass}`}
              >
                {thumb}
              </div>
            ) : (
              <Link
                key={step.key}
                ref={step.key === focusKey ? focusRef : undefined}
                to={`/academy/${topicId}/${course.id}/${lesson.id}`}
                className={`ac-side-item ${stateClass}`}
              >
                {thumb}
              </Link>
            );
          }

          // Quiz / practice lab → compact sub-step row.
          const isPractice = type === 'practice';
          let label = `${lesson.quiz ? lesson.quiz.questions.length : 0} questions`;
          let kicker = 'Mini-Quiz';
          let to = `/academy/${topicId}/${course.id}/${lesson.id}/quiz`;
          let glyph = <IconQuiz size={14} />;
          if (isPractice) {
            label = lesson.practiceLab.title;
            kicker = `Practice · ${MODE_LABEL[lesson.practiceLab.mode] || ''}`.trim();
            to = `/academy/${topicId}/${course.id}/${lesson.id}/practice`;
            glyph = <IconLab size={14} />;
          }

          const rowInner = (
            <>
              <span
                className={`ac-side-sub-icon ${done ? 'is-done' : ''} ${locked ? 'is-locked' : ''} ${
                  isPractice ? 'is-practice' : ''
                }`}
              >
                {done ? <IconCheck size={12} /> : locked ? <IconLock size={12} /> : glyph}
              </span>
              <span className="ac-side-sub-text">
                <span className="ac-side-sub-kicker">{kicker}</span>
                <span className="ac-side-sub-label">{label}</span>
              </span>
            </>
          );

          return locked ? (
            <div
              key={step.key}
              ref={step.key === focusKey ? focusRef : undefined}
              className="ac-side-sub is-locked"
            >
              {rowInner}
            </div>
          ) : (
            <Link
              key={step.key}
              ref={step.key === focusKey ? focusRef : undefined}
              to={to}
              className={`ac-side-sub ${active ? 'is-active' : ''}`}
            >
              {rowInner}
            </Link>
          );
        })}
      </div>
    </aside>
  );
};

export default CourseSidebar;
